import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Download } from 'lucide-react';

const AdminAiSettings = ({ backendUrl }) => {
  const [settings, setSettings] = useState({
    provider: 'google',
    apiKey: '',
    selectedModel: '',
    deepseekApiKey: '',
    deepseekSelectedModel: '',
    openRouterApiKey: '',
    openRouterSelectedModel: '',
    imageProvider: 'google',
    googleImageApiKey: '',
    deepseekImageApiKey: '',
    openRouterImageApiKey: '',
    imageSelectedModel: '',
    openRouterImageSelectedModel: '',
  });
  const [models, setModels] = useState([]);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState('');
  const [status, setStatus] = useState('');

  useEffect(() => {
    fetchSettings();
    fetchModels();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${backendUrl}/admin/ai/settings`, { withCredentials: true });
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching AI settings:', error);
      setStatus('Błąd podczas ładowania ustawień.');
    }
  };

  const fetchModels = async () => {
    try {
      const response = await axios.get(`${backendUrl}/admin/ai/models`, { withCredentials: true });
      setModels(response.data.models || []);
    } catch (error) {
      console.error('Error fetching AI models:', error);
      setStatus('Błąd podczas ładowania listy modeli.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveSettings = async () => {
    try {
      await axios.post(`${backendUrl}/admin/ai/settings`, settings, { withCredentials: true });
      setStatus('Ustawienia zapisane pomyślnie.');
    } catch (error) {
      console.error('Error saving AI settings:', error);
      setStatus('Błąd podczas zapisywania ustawień.');
    }
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestResult('');
    try {
      const response = await axios.get(`${backendUrl}/admin/ai/test`, { withCredentials: true });
      setTestResult(response.data.message);
    } catch (error) {
      setTestResult(error.response?.data?.message || 'Błąd połączenia.');
    }
    setIsTesting(false);
  };

  const handleDownloadLogs = async () => {
    try {
      const response = await axios.get(`${backendUrl}/admin/ai-request-logs/download`, {
        withCredentials: true,
        responseType: 'blob', // Ważne, aby otrzymać plik jako blob
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'ai_requests.log');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading logs:', error);
      setStatus('Błąd podczas pobierania logów.');
    }
  };

  return (
    <div className="admin-ai-settings">
      <h2>Ustawienia Sztucznej Inteligencji</h2>
      {status && <p className="status-message">{status}</p>}

      {/* Sekcja głównego dostawcy AI */}
      <div className="ai-settings-section">
        <h3>Główny dostawca AI (Tekst)</h3>
        <select name="provider" value={settings.provider} onChange={handleInputChange}>
          <option value="google">Google</option>
          <option value="deepseek">DeepSeek</option>
          <option value="openrouter">OpenRouter</option>
        </select>

        {/* Pola dla Google */}
        {settings.provider === 'google' && (
          <>
            <input type="text" name="apiKey" placeholder="Google API Key" value={settings.apiKey} onChange={handleInputChange} />
            <select name="selectedModel" value={settings.selectedModel} onChange={handleInputChange}>
              {models.map(model => <option key={model.name} value={model.name}>{model.displayName}</option>)}
            </select>
          </>
        )}

        {/* Pola dla DeepSeek */}
        {settings.provider === 'deepseek' && (
          <>
            <input type="text" name="deepseekApiKey" placeholder="DeepSeek API Key" value={settings.deepseekApiKey} onChange={handleInputChange} />
            <input type="text" name="deepseekSelectedModel" placeholder="DeepSeek Model" value={settings.deepseekSelectedModel} onChange={handleInputChange} />
          </>
        )}

        {/* Pola dla OpenRouter */}
        {settings.provider === 'openrouter' && (
          <>
            <input type="text" name="openRouterApiKey" placeholder="OpenRouter API Key" value={settings.openRouterApiKey} onChange={handleInputChange} />
            <input type="text" name="openRouterSelectedModel" placeholder="OpenRouter Model" value={settings.openRouterSelectedModel} onChange={handleInputChange} />
          </>
        )}
      </div>

      {/* Sekcja dostawcy AI dla obrazów */}
      <div className="ai-settings-section">
        <h3>Dostawca AI (Obrazy)</h3>
        <select name="imageProvider" value={settings.imageProvider} onChange={handleInputChange}>
          <option value="google">Google</option>
          <option value="deepseek">DeepSeek</option>
          <option value="openrouter">OpenRouter</option>
        </select>
        
        {/* Pola dla Google (obrazy) */}
        {settings.imageProvider === 'google' && (
          <>
            <input type="text" name="googleImageApiKey" placeholder="Google Image API Key" value={settings.googleImageApiKey} onChange={handleInputChange} />
            <input type="text" name="imageSelectedModel" placeholder="Google Image Model" value={settings.imageSelectedModel} onChange={handleInputChange} />
          </>
        )}

        {/* Pola dla DeepSeek (obrazy) */}
        {settings.imageProvider === 'deepseek' && (
          <>
            <input type="text" name="deepseekImageApiKey" placeholder="DeepSeek Image API Key" value={settings.deepseekImageApiKey} onChange={handleInputChange} />
          </>
        )}

        {/* Pola dla OpenRouter (obrazy) */}
        {settings.imageProvider === 'openrouter' && (
          <>
            <input type="text" name="openRouterImageApiKey" placeholder="OpenRouter Image API Key" value={settings.openRouterImageApiKey} onChange={handleInputChange} />
            <input type="text" name="openRouterImageSelectedModel" placeholder="OpenRouter Image Model" value={settings.openRouterImageSelectedModel} onChange={handleInputChange} />
          </>
        )}
      </div>

      <div className="ai-settings-actions">
        <button onClick={handleSaveSettings}>Zapisz Ustawienia</button>
        <button onClick={handleTestConnection} disabled={isTesting}>
          {isTesting ? 'Testowanie...' : 'Testuj Połączenie'}
        </button>
        {testResult && <p>{testResult}</p>}
      </div>

      <div className="ai-settings-section">
        <h3>Logi Zapytań Klientów</h3>
        <p>Pobierz plik z logami wszystkich zapytań wysyłanych do AI przez klientów.</p>
        <button onClick={handleDownloadLogs}>
          <Download size={16} /> Pobierz Logi
        </button>
      </div>
    </div>
  );
};

export default AdminAiSettings;
